import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { deleteOtaArtifact, downloadOtaArtifact, getAllDeviceModels, getAllOtaArtifacts } from "../../commonApi";
import { toast } from "react-toastify";
import { Loader } from "../../Loader";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faRotateRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { DeleteButton } from "../../components/DeleteButton";
import { DownloadButton } from "../../components/DownloadButton";
import "../../../src/assets/css/OtaArtifacts.css";
import "../../../src/assets/css/Action.css";
import { extractErrorMessage, extractSuccessMessage } from "../../Utils/ExtractMessages";
import { DeleteModal } from "../../components/DeleteModal";
import { ArtifactDeleteBody } from "./ArtifactDeleteBody";
import { CreateArtifactModal } from "./CreateArtifactModal";
import { UpdateArtifactModal } from "./UpdateArtifactModal";

function OtaArtifacts() {

    const [page, setPage] = useState(0);
    const [sortBy, setSortBy] = useState("createdOn");
    const [sortOrder, setSortOrder] = useState("desc");
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [data, setData] = useState([]);
    const [deviceModelList, setDeviceModelList] = useState([]);
    const [deviceModelMap, setDeviceModelMap] = useState({});
    const [deviceModelFilter, setDeviceModelFilter] = useState("");
    const [artifactTypeFilter, setArtifactTypeFilter] = useState("");
    const [releaseChannelFilter, setReleaseChannelFilter] = useState("");
    const [otaTypeFilter, setOtaTypeFilter] = useState("");
    const [vendorIdFilter, setVendorIdFilter] = useState("");
    const [productIdFilter, setProductIdFilter] = useState("");
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedArtifact, setSelectedArtifact] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const latestRequestRef = useRef(0);

    const formatVersionDisplay = (row) => {
        return row.version || "-";
    };

    const formatMinRequiredVersion = (row) => {
        const major = row.minRequiredVersion?.major ?? row.minReqMajor;
        const minor = row.minRequiredVersion?.minor ?? row.minReqMinor;
        const patch = row.minRequiredVersion?.patch ?? row.minReqPatch;
        if (major === undefined && minor === undefined && patch === undefined) return "-";
        return `${major ?? 0}.${minor ?? 0}.${patch ?? 0}`;
    };

    const formatMinHwVersion = (row) => {
        const major = row.minHwVersion?.major ?? row.minHwMajor;
        const minor = row.minHwVersion?.minor ?? row.minHwMinor;
        if (major === undefined && minor === undefined) return "-";
        return `${major ?? 0}.${minor ?? 0}`;
    };

    const formatBytes = (bytes) => {
        if (bytes === undefined || bytes === null) return "-";
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const columns = [
        {
            name: "Artifact Name",
            selector: (row) => row.artifactName || "-",
            sortable: true,
            sortField: "artifactName",
            width: "170px",
            cell: (row) => (
                <span className="truncate-cell-text" title={row.artifactName || "-"}>
                    {row.artifactName || "-"}
                </span>
            ),
        },
        { name: "Device Model", selector: (row) => row.deviceModelName, sortable: false, sortField: "deviceModelName" },
        { name: "Artifact Type", selector: (row) => row.artifactType, sortable: true, sortField: "artifactType" },
        { name: "OTA Type", selector: (row) => row.otaType || "-", sortable: true, sortField: "otaType" },
        { name: "Vendor ID", selector: (row) => row.vendorId || "-", sortable: true, sortField: "vendorId" },
        { name: "Product ID", selector: (row) => row.productId || "-", sortable: true, sortField: "productId" },
        { name: "Version", selector: (row) => formatVersionDisplay(row), sortable: true, sortField: "version" },
        { name: "Min Required Version", selector: (row) => formatMinRequiredVersion(row), sortable: false },
        { name: "Min HW Version", selector: (row) => formatMinHwVersion(row), sortable: false },
        { name: "Release Channel", selector: (row) => row.releaseChannel, sortable: true, sortField: "releaseChannel" },
        { name: "Compulsory", selector: (row) => (row.isCompulsory ? "Yes" : "No"), sortable: false },
        { name: "Checksum Verified", selector: (row) => (row.checksumVerified ? "Yes" : "No"), sortable: false },
        { name: "Payload Length", selector: (row) => formatBytes(row.payloadLength), sortable: true, sortField: "payloadLength" },
        { name: "Created On", selector: (row) => formatDate(row.createdOn), sortable: true, sortField: "createdOn", width: "200px" },
        {
            name: "Actions", cell: (row) => (
                <div className="action-icons">
                    <button onClick={() => handleEdit(row)} title="Edit" className="btn action-icon">
                        <FontAwesomeIcon icon={faPen} style={{ color: '#007bff', fontSize: '16px' }} />
                    </button>
                    <div onClick={() => handleDelete(row)} title="Delete" className="btn action-icon">
                        <DeleteButton />
                    </div>
                    <div onClick={() => handleDownload(row)} title="Download" className="btn action-icon">
                        {downloadingId === row.id ? (
                            <Loader />
                        ) : (
                            <DownloadButton />
                        )}
                    </div>
                </div>
            ),
            center: true,
            width: "130px",
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const handlePageChange = (currentPage) => {
        setPage(currentPage - 1);
    };

    const handlePageSizeChange = (currentPageSize) => {
        setPageSize(currentPageSize);
        setPage(0);
    };

    const handleSort = (column, sortDirection) => {
        setSortBy(column.sortField || column.selector);
        setSortOrder(sortDirection);
    };

    const handleEdit = (row) => {
        setSelectedArtifact(row);
        setIsEditOpen(true);
    };

    const handleDelete = (row) => {
        setSelectedArtifact(row);
        setIsDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedArtifact?.id) return;

        try {
            setIsDeleting(true);

            const response = await deleteOtaArtifact(selectedArtifact.id);

            toast.success(extractSuccessMessage(response, "Artifact deleted successfully."));

            setIsDeleteOpen(false);
            setSelectedArtifact(null);

        } catch (error) {
            const msg = extractErrorMessage(error)
            if (error?.response?.status === 404) {
                toast.warn(msg);
                setIsDeleteOpen(false);
                setSelectedArtifact(null);
                return;
            }
            toast.error(msg);
        } finally {
            await fetchData();
            setIsDeleting(false);
        }
    };

    const handleDownload = async (row) => {
        try {
            setDownloadingId(row.id);

            const params = {
                expiryMinutes: 60
            };
            const response = await downloadOtaArtifact(row.id, params);
            const { downloadUrl, fileName } = response.data.data;
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success(`${fileName} downloaded successfully`);

        } catch (error) {
            toast.error(extractErrorMessage(error));
        } finally {
            setDownloadingId(null);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "-";
        return moment(timestamp).format("YYYY-MM-DD HH:mm:ss");
    };

    const fetchDeviceModels = async () => {
        try {
            const response = await getAllDeviceModels(0, 100, "createdDate", "DESC");
            return response.data.data.content || [];
        } catch (error) {
            toast.error(extractErrorMessage(error));
            return [];
        }
    };

    const initDeviceModels = async () => {
        setLoading(true);
        const models = await fetchDeviceModels();
        const modelMap = createDeviceModelMap(models);

        setDeviceModelList(models);
        setDeviceModelMap(modelMap);
        setLoading(false);
    };

    const fetchArtifactsData = async () => {
        try {
            const params = {
                pageNo: page,
                pageSize: pageSize,
                sortBy: sortBy,
                sortOrder: sortOrder,
                deviceModelId: deviceModelFilter || undefined,
                artifactType: artifactTypeFilter || undefined,
                releaseChannel: releaseChannelFilter || undefined,
                otaType: otaTypeFilter || undefined,
                vendorId: vendorIdFilter || undefined,
                productId: productIdFilter || undefined,
            };

            const response = await getAllOtaArtifacts(params);
            return {
                artifacts: response.data.data.content || [],
                total: response.data.data.totalElements || 0,
            };
        } catch (error) {
            throw error;
        }
    };

    const createDeviceModelMap = (models) => {
        return models.reduce((acc, model) => {
            acc[model.id] = model.name;
            return acc;
        }, {});
    };

    const mapArtifactsWithDeviceModel = (artifacts, modelMap) => {
        return artifacts.map(item => ({
            ...item,
            deviceModelName: modelMap[item.deviceModelId] || "-"
        }));
    };

    const fetchData = async () => {
        const requestId = ++latestRequestRef.current;
        setLoading(true);

        try {
            const artifactResult = await fetchArtifactsData();
            if (requestId !== latestRequestRef.current) return;

            const finalData = mapArtifactsWithDeviceModel(
                artifactResult.artifacts,
                deviceModelMap
            );

            setTotalRows(artifactResult.total);
            setData(finalData);

        } catch (error) {
            if (requestId !== latestRequestRef.current) return;
            toast.error(extractErrorMessage(error));
        } finally {
            if (requestId === latestRequestRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (Object.keys(deviceModelMap).length === 0) return;
        fetchData();
    }, [page, pageSize, sortBy, sortOrder, deviceModelFilter, artifactTypeFilter, releaseChannelFilter, otaTypeFilter, vendorIdFilter, productIdFilter, deviceModelMap]);

    useEffect(() => {
        initDeviceModels();
    }, []);

    useEffect(() => {
        setPage(0);
    }, [deviceModelFilter, artifactTypeFilter, releaseChannelFilter, otaTypeFilter, vendorIdFilter, productIdFilter]);

    return (
        <div className="custom_tab_wrapper">
            <div className="action_cta">
                <ul className="nav nav-tabs" id="myTab" role="tablist" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <li className="nav-item" role="presentation">
                        <button className="nav-link active" id="firmware-tab" data-bs-toggle="tab" data-bs-target="#firmware" type="button" role="tab" aria-controls="firmware" aria-selected="true">
                            Artifacts : {totalRows}
                        </button>
                    </li>
                </ul>
            </div>
            <div className="table-toolbar custom_table_action">
                <div className="toolbar-left">
                    <select
                        value={deviceModelFilter}
                        onChange={(e) => setDeviceModelFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Device Models</option>
                        {deviceModelList.map(model => (
                            <option key={model.id} value={model.id}>
                                {model.name}
                            </option>
                        ))}
                    </select>
                    {/* <select
                        value={artifactTypeFilter}
                        onChange={(e) => setArtifactTypeFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Artifact Types</option>
                        <option value="FIRMWARE">Firmware</option>
                        <option value="SOFTWARE">Software</option>
                        <option value="AI_MODEL">AI_MODEL </option>
                    </select>
                    <select
                        value={releaseChannelFilter}
                        onChange={(e) => setReleaseChannelFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Release Channels</option>
                        <option value="ALPHA">ALPHA</option>
                        <option value="BETA">Beta</option>
                        <option value="GA">GA</option>
                    </select> */}
                    <select
                        value={otaTypeFilter}
                        onChange={(e) => setOtaTypeFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All OTA Types</option>
                        <option value="FULL">FULL</option>
                        <option value="APP">APP</option>
                    </select>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Vendor ID"
                        value={vendorIdFilter}
                        onChange={(e) => setVendorIdFilter(e.target.value)}
                    />
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Product ID"
                        value={productIdFilter}
                        onChange={(e) => setProductIdFilter(e.target.value)}
                    />
                </div>

                <div className="toolbar-right">
                    <button onClick={fetchData} className="btn light_primary_btn" title="Refresh" disabled={loading}>
                        <FontAwesomeIcon icon={faRotateRight} className={loading ? "spin-icon" : ""} />
                    </button>
                    <button className="btn primary_btn" title="Create New Artifact" onClick={() => { setIsCreateOpen(true) }}>
                        <FontAwesomeIcon icon={faPlus} />
                    </button>
                </div>
            </div>
            <div className="tab-pane fade show active" id="home" role="tabpanel">
                <div className="tab_content_wrapper full_height_tab">
                    <div className="table-responsive h-100vh-150px">
                        {loading ?
                            <div className="spin-loader">
                                <Loader />
                            </div> :
                            <div style={{ width: '100%', overflowX: 'auto' }}>
                                <div className="custom_table customs-table">
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        progressPending={loading}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePageSizeChange}
                                        pagination
                                        paginationServer
                                        paginationDefaultPage={page + 1}
                                        paginationPerPage={pageSize}
                                        paginationTotalRows={totalRows}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        onSort={handleSort}
                                        defaultSortAsc={sortOrder === "asc"}
                                        sortServer
                                        fixedHeader
                                        fixedHeaderScrollHeight="425px"
                                    />
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => {
                    if (!isDeleting) {
                        setIsDeleteOpen(false);
                        setSelectedArtifact(null);
                    }
                }}
                onConfirmDelete={handleConfirmDelete}
                isDeleteDisabled={isDeleting}
                modalTitle="Delete Artifact"
                modalBody={<ArtifactDeleteBody artifact={selectedArtifact} />}
            />
            <CreateArtifactModal
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                deviceModels={deviceModelList}
                onSuccess={fetchData}
            />
            <UpdateArtifactModal
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                artifact={selectedArtifact}
                deviceModels={deviceModelList}
                onSuccess={fetchData}
            />
        </div>
    );
}

export default OtaArtifacts;